import { Box, Snackbar, Stack, Tooltip } from '@mui/material'
import type { CustomerUser } from '@einwert/valuation-api-client'
import type { OrderListItem as OrderListItemType } from 'ClientPortal/src/pages/Orders/OrderList/OrderListPageContent.data'
import {
    Alert,
    assembleAssetTitle,
    AssetList,
    ContentChip,
    EmptyState,
    ListCard,
    ListItem,
    LoadingSkeleton,
    OrderStatus,
    OrderStatusLabel,
    tokens,
} from 'ComponentLibrary'
import { IconButton, IconButtonWithOptions } from 'ComponentLibrary/src/piazza'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { isEmpty } from 'lodash'
import { generatePath, Link, useNavigate } from 'react-router-dom'
import { AddressInfoCell } from './OrderListItemCell/AddressInfoCell'
import { MandatorInfoCell } from './OrderListItemCell/MandatorInfoCell'
import { OrderInfoCell } from './OrderListItemCell/OrderInfoCell'

import { hasSomeFilter, useOrderListPageContent } from './OrderListPageContent.data'
import { z } from 'zod'
import { DateRangeSchema, OrderListFilterStateSchema, PortfolioMetadataSchema } from 'ClientPortal/src/pages/schemas.zod'
import {
    ArrowCounterClockwiseIcon,
    ArrowUpRightIcon,
    CaretDownIcon,
    ChatTextIcon,
    CheckCircleIcon,
    HighlighterIcon,
    PaperPlaneTiltIcon,
    SealCheckIcon,
} from '@phosphor-icons/react'
import { memo, useCallback, useMemo, useState } from 'react'
import OrderObserverToggleButton from './OrderObserverToggle/OrderObserverToggleButton'
import { DYNAMIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import { usePatchPlausibility } from 'ClientPortal/src/api/Queries/plausibility'
import { HandoverModal } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationPlausibility/HandoverModal'
import { ApprovalModal } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationPlausibility/ApprovalModal'
import { InternalApprovalModal } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationPlausibility/InternalApprovalModal'
import { useGetAppraiserUsers } from 'ClientPortal/src/api/Queries/users'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { useTranslation } from 'react-i18next'
import { isPlausibilityVisibleToAppraisers } from '@einwert/valuation-api-client'

export type DateRange = z.infer<typeof DateRangeSchema>
export type PortfolioMetadata = z.infer<typeof PortfolioMetadataSchema>
export type FilterState = z.infer<typeof OrderListFilterStateSchema>
export type OrderSortOption = 'orderDate' | 'alphabetical'

type Category = 'open' | 'closed' | 'cancelled'

const shouldShowPlausibility = (order: OrderListItemType) => order.valuation_status === 'in_review' && !!order.plausibility_uuid

const getDraftChipLabel = (order: OrderListItemType, fallbackLabel: string) => {
    const draftTitle = order.latest_draft_title?.split(' - ')[0]?.trim()

    if (!draftTitle) {
        return fallbackLabel
    }

    const draftVersion = draftTitle.replace(/^(Entwurf|Draft)\s*/i, '').trim()
    return draftVersion || draftTitle
}

type OrderListPageContentProps = {
    filterState: FilterState
    defaultStatuses: OrderStatus[]
    customerUsers: CustomerUser[]
    category: Category
    setCategory: (category: Category) => void
    resetFilters: () => void
    sortOption: OrderSortOption
}

const OrderListPageContent = ({
    filterState,
    defaultStatuses,
    customerUsers,
    category,
    setCategory,
    resetFilters,
    sortOption,
}: OrderListPageContentProps) => {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderListPage' })
    const [successMessage, setSuccessMessage] = useState<string>()
    const [activeOrderForHandOver, setActiveOrderForHandOver] = useState<OrderListItemType>()
    const [activeOrderForApproval, setActiveOrderForApproval] = useState<OrderListItemType>()
    const [activeOrderForInternalApproval, setActiveOrderForInternalApproval] = useState<OrderListItemType>()
    const [selectedAppraiserId, setSelectedAppraiserId] = useState<number>()
    const { isPlausibilityInternalApprovalEnabled } = useFlags()
    const emptyStateMap = {
        open: {
            label: t('emptyStates.noOpenOrders'),
            buttonText: t('emptyStates.showClosedOrders'),
            buttonAction: () => setCategory('closed'),
        },
        closed: {
            label: t('emptyStates.noClosedOrders'),
            buttonText: t('emptyStates.showCancelledOrders'),
            buttonAction: () => setCategory('cancelled'),
        },
        cancelled: {
            label: t('emptyStates.noCancelledOrders'),
            buttonText: t('emptyStates.showOpenOrders'),
            buttonAction: () => setCategory('open'),
        },
    }

    const { orders, isPending: isOrderOverviewsPending } = useOrderListPageContent({
        filters: filterState,
        defaultStatuses,
    })

    const sortedOrders = useMemo(() => {
        if (sortOption === 'alphabetical') {
            return [...orders].sort((orderA, orderB) => {
                const titleA = assembleAssetTitle({ ...orderA.address, customer_asset_reference: orderA.customer_asset_reference })
                const titleB = assembleAssetTitle({ ...orderB.address, customer_asset_reference: orderB.customer_asset_reference })
                return titleA.localeCompare(titleB, 'de', { sensitivity: 'base' })
            })
        }

        return orders
    }, [orders, sortOption])

    const activeOrder = activeOrderForHandOver ?? activeOrderForApproval ?? activeOrderForInternalApproval
    const isHandoverOpen = !!activeOrderForHandOver
    const isApprovalOpen = !!activeOrderForApproval
    const isInternalApprovalOpen = !!activeOrderForInternalApproval

    const closeActivePlausibilityModal = useCallback(() => {
        setActiveOrderForHandOver(undefined)
        setActiveOrderForApproval(undefined)
        setActiveOrderForInternalApproval(undefined)
        setSelectedAppraiserId(undefined)
    }, [])

    const shouldLoadAppraiserUsers = !!activeOrder && (isHandoverOpen || isApprovalOpen || isInternalApprovalOpen)
    const { appraiserUsers, isAppraiserUsersPending } = useGetAppraiserUsers({
        orderId: shouldLoadAppraiserUsers ? activeOrder.uuid : undefined,
        enabled: shouldLoadAppraiserUsers,
    })
    const { patchPlausibility, isPatchingPlausibility } = usePatchPlausibility({
        valuationId: activeOrder?.latest_draft_uuid,
        onSuccess: closeActivePlausibilityModal,
    })
    const participantIds = activeOrder?.participant_ids ?? []
    const appraiserIds = new Set(appraiserUsers.map(({ id }) => id))
    const appraiserUUIDs = new Set(appraiserUsers.map(({ uuid }) => uuid))
    const hasAppraiserInObserversOrParticipants =
        participantIds.some((participantId) => appraiserIds.has(participantId)) ||
        (activeOrder?.observers.some((observerId) => appraiserUUIDs.has(observerId)) ?? false)
    const hasNewAnnotations = (activeOrder?.annotation_count ?? 0) > 0
    const appraiserSelectionCopy = {
        title: t('plausibility.appraiserSelection.title'),
        description: t('plausibility.appraiserSelection.description'),
        loading: t('plausibility.appraiserSelection.loading'),
        empty: t('plausibility.appraiserSelection.empty'),
        selectLabel: t('plausibility.appraiserSelection.selectLabel'),
    }

    const openHandoverModal = useCallback((order: OrderListItemType) => {
        setSelectedAppraiserId(undefined)
        setActiveOrderForApproval(undefined)
        setActiveOrderForInternalApproval(undefined)
        setActiveOrderForHandOver(order)
    }, [])

    const openApprovalModal = useCallback((order: OrderListItemType) => {
        setSelectedAppraiserId(undefined)
        setActiveOrderForHandOver(undefined)
        setActiveOrderForInternalApproval(undefined)
        setActiveOrderForApproval(order)
    }, [])

    const openInternalApprovalModal = useCallback((order: OrderListItemType) => {
        setSelectedAppraiserId(undefined)
        setActiveOrderForHandOver(undefined)
        setActiveOrderForApproval(undefined)
        setActiveOrderForInternalApproval(order)
    }, [])

    const handleObserverSuccess = useCallback(() => {
        setSuccessMessage(t('snackbar.observerSaved'))
    }, [t])

    const getUpdatedParticipantIds = () => {
        if (!activeOrder) {
            return undefined
        }

        if (hasAppraiserInObserversOrParticipants || selectedAppraiserId === undefined || participantIds.includes(selectedAppraiserId)) {
            return undefined
        }

        return [...participantIds, selectedAppraiserId]
    }

    const setIsHandedOver = (newHandoverState: boolean, idsToUpdate?: number[]) => {
        if (!activeOrder?.latest_draft_uuid || activeOrder.latest_draft_uuid === 'undefined') {
            return
        }

        patchPlausibility([
            ...(idsToUpdate
                ? [
                      {
                          op: 'replace' as const,
                          path: '/participant_ids',
                          value: idsToUpdate,
                      },
                  ]
                : []),
            {
                op: 'replace',
                path: '/is_handed_over',
                value: newHandoverState,
            },
        ])
    }

    const setIsApproved = (newApprovalState: boolean, idsToUpdate?: number[]) => {
        if (!activeOrder?.latest_draft_uuid || activeOrder.latest_draft_uuid === 'undefined') {
            return
        }

        patchPlausibility([
            ...(idsToUpdate
                ? [
                      {
                          op: 'replace' as const,
                          path: '/participant_ids',
                          value: idsToUpdate,
                      },
                  ]
                : []),
            {
                op: 'replace',
                path: '/is_approved',
                value: newApprovalState,
            },
        ])
    }

    const setIsInternallyApproved = (newApprovalState: boolean, idsToUpdate?: number[]) => {
        if (!activeOrder?.latest_draft_uuid || activeOrder.latest_draft_uuid === 'undefined') {
            return
        }

        patchPlausibility([
            ...(idsToUpdate
                ? [
                      {
                          op: 'replace' as const,
                          path: '/participant_ids',
                          value: idsToUpdate,
                      },
                  ]
                : []),
            {
                op: 'replace',
                path: '/is_internally_approved',
                value: newApprovalState,
            },
        ])
    }

    const handleHandoverConfirm = () => {
        setIsHandedOver(true, getUpdatedParticipantIds())
    }

    const handleApprovalConfirm = () => {
        setIsApproved(true, getUpdatedParticipantIds())
    }

    const handleInternalApprovalConfirm = () => {
        setIsInternallyApproved(true, getUpdatedParticipantIds())
    }

    if (isOrderOverviewsPending) {
        return (
            <Stack direction="column" gap={tokens.SizingSpacing150}>
                <LoadingSkeleton count={4} height="85px" width="100%" sx={{ transform: 'none' }} />
            </Stack>
        )
    }

    if (isEmpty(orders)) {
        return (
            <Box width="100%" alignItems="center">
                {hasSomeFilter(filterState) ? (
                    <EmptyState
                        title={t('emptyStates.noFilteredOrders')}
                        size="enormous"
                        button={{
                            label: t('emptyStates.resetFilters'),
                            action: resetFilters,
                            rightIcon: <ArrowCounterClockwiseIcon />,
                        }}
                    />
                ) : (
                    <EmptyState
                        title={emptyStateMap[category].label}
                        size="enormous"
                        button={{
                            label: emptyStateMap[category].buttonText,
                            action: emptyStateMap[category].buttonAction,
                            rightIcon: <ArrowUpRightIcon />,
                        }}
                    />
                )}
            </Box>
        )
    }
    return (
        <>
            <AssetList aria-label="assetList">
                {sortedOrders.map((order) => (
                    <OrderListItem
                        key={order.uuid}
                        order={order}
                        customerUsers={customerUsers}
                        onObserverSuccess={handleObserverSuccess}
                        onOpenHandoverModal={openHandoverModal}
                        onOpenApprovalModal={openApprovalModal}
                        onOpenInternalApprovalModal={openInternalApprovalModal}
                        showInternalApproval={isPlausibilityInternalApprovalEnabled}
                    />
                ))}
            </AssetList>
            {activeOrder && (
                <>
                    <HandoverModal
                        isHandoverOpen={isHandoverOpen}
                        onConfirm={handleHandoverConfirm}
                        onClose={closeActivePlausibilityModal}
                        isPersisting={isPatchingPlausibility}
                        hasAppraiserInObserversOrParticipants={hasAppraiserInObserversOrParticipants}
                        appraiserUsers={appraiserUsers}
                        selectedAppraiserId={selectedAppraiserId}
                        setSelectedAppraiserId={setSelectedAppraiserId}
                        isAppraiserUsersPending={isAppraiserUsersPending}
                        copy={{
                            title: t('plausibility.handoverModal.title'),
                            primaryButton: t('plausibility.handoverModal.primaryButton'),
                            secondaryButton: t('plausibility.modal.cancel'),
                            imageAlt: t('plausibility.handoverModal.imageAlt'),
                            makeAnnotationsVisible: t('plausibility.handoverModal.makeAnnotationsVisible'),
                            requestNewDraft: t('plausibility.handoverModal.requestNewDraft'),
                            infoText: t('plausibility.handoverModal.infoText'),
                            appraiserSelection: appraiserSelectionCopy,
                        }}
                    />
                    <ApprovalModal
                        isApprovalOpen={isApprovalOpen}
                        onClose={closeActivePlausibilityModal}
                        onConfirm={handleApprovalConfirm}
                        hasNewAnnotations={hasNewAnnotations}
                        commentsAreAlreadyVisible={isPlausibilityVisibleToAppraisers(activeOrder)}
                        isPersisting={isPatchingPlausibility}
                        hasAppraiserInObserversOrParticipants={hasAppraiserInObserversOrParticipants}
                        appraiserUsers={appraiserUsers}
                        selectedAppraiserId={selectedAppraiserId}
                        setSelectedAppraiserId={setSelectedAppraiserId}
                        isAppraiserUsersPending={isAppraiserUsersPending}
                        copy={{
                            title: t('plausibility.approvalModal.title'),
                            primaryButton: t('plausibility.approvalModal.primaryButton'),
                            secondaryButton: t('plausibility.modal.cancel'),
                            imageAlt: t('plausibility.approvalModal.imageAlt'),
                            makeAnnotationsVisible: t('plausibility.approvalModal.makeAnnotationsVisible'),
                            requestFinalizationWithAnnotations: t('plausibility.approvalModal.requestFinalizationWithAnnotations'),
                            requestFinalization: t('plausibility.approvalModal.requestFinalization'),
                            infoText: t('plausibility.approvalModal.infoText'),
                            confirmationLabel: t('plausibility.approvalModal.confirmationLabel'),
                            appraiserSelection: appraiserSelectionCopy,
                        }}
                    />
                    <InternalApprovalModal
                        isOpen={isInternalApprovalOpen}
                        onClose={closeActivePlausibilityModal}
                        onConfirm={handleInternalApprovalConfirm}
                        isPersisting={isPatchingPlausibility}
                        commentsAreAlreadyVisible={isPlausibilityVisibleToAppraisers(activeOrder)}
                        hasAppraiserInObserversOrParticipants={hasAppraiserInObserversOrParticipants}
                        appraiserUsers={appraiserUsers}
                        selectedAppraiserId={selectedAppraiserId}
                        setSelectedAppraiserId={setSelectedAppraiserId}
                        isAppraiserUsersPending={isAppraiserUsersPending}
                        copy={{
                            title: t('plausibility.internalApprovalModal.title'),
                            primaryButton: t('plausibility.internalApprovalModal.primaryButton'),
                            secondaryButton: t('plausibility.modal.cancel'),
                            imageAlt: t('plausibility.internalApprovalModal.imageAlt'),
                            fundApproval: t('plausibility.internalApprovalModal.fundApproval'),
                            confirmationLabel: t('plausibility.internalApprovalModal.confirmationLabel'),
                            appraiserSelection: appraiserSelectionCopy,
                        }}
                    />
                </>
            )}
            <Snackbar
                anchorOrigin={{
                    horizontal: 'center',
                    vertical: 'bottom',
                }}
                open={successMessage !== undefined}
                onClose={() => setSuccessMessage(undefined)}
                autoHideDuration={4000}
            >
                <div>
                    <Alert severity="success" size="small">
                        {successMessage}
                    </Alert>
                </div>
            </Snackbar>
        </>
    )
}

const OrderListItem = memo(
    ({
        order,
        customerUsers,
        onObserverSuccess,
        onOpenHandoverModal,
        onOpenApprovalModal,
        onOpenInternalApprovalModal,
        showInternalApproval,
    }: {
        order: OrderListItemType
        customerUsers: CustomerUser[]
        onObserverSuccess?: () => void
        onOpenHandoverModal: (order: OrderListItemType) => void
        onOpenApprovalModal: (order: OrderListItemType) => void
        onOpenInternalApprovalModal: (order: OrderListItemType) => void
        showInternalApproval: boolean
    }) => {
        const { t } = useTranslation('pages', { keyPrefix: 'orders.orderListPage' })
        const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
        const navigate = useNavigate()

        const isOrderInPlausibility = shouldShowPlausibility(order)
        const showPlausibility = !order.external_participant_mailbox
        const isHandedOver = !!order.is_handed_over
        const isApproved = !!order.is_approved
        const isInternallyApproved = !!order.is_internally_approved
        const isVisibleToAppraisers = isPlausibilityVisibleToAppraisers(order)
        const draftChipLabel = getDraftChipLabel(order, t('plausibility.draft'))
        const draftChipTooltipLabel = /^(entwurf|draft)\b/i.test(draftChipLabel)
            ? draftChipLabel
            : t('plausibility.draftWithVersion', { version: draftChipLabel })
        const annotationCount = order.annotation_count ?? 0
        const annotationCountTooltipLabel = t('plausibility.annotationCount', { count: annotationCount })
        const hasAnnotationsToHandover = annotationCount > 0
        const isHandoverDisabled = isVisibleToAppraisers || !hasAnnotationsToHandover
        const handoverTooltip = isVisibleToAppraisers
            ? t('plausibility.annotationsAlreadyShared')
            : hasAnnotationsToHandover
              ? t('plausibility.shareAnnotations')
              : t('plausibility.noAnnotations')

        const plausibilityPath =
            order.asset_uuid && order.latest_draft_uuid
                ? `/${generatePath(DYNAMIC_APP_PATHS.valuationPlausibilityPage, {
                      assetId: order.asset_uuid,
                      valuationId: order.latest_draft_uuid,
                  })}`
                : undefined

        const shouldShowPlausibilityActions = showPlausibility && isOrderInPlausibility && !isApproved && !!plausibilityPath

        return (
            <ListItem>
                <Link to={`/orders/${order.uuid}/overview`} style={{ display: 'flex', flex: 1, minWidth: 0, cursor: 'pointer' }}>
                    <ListCard
                        imageSrc={order.asset_title_image?.thumbnail_url}
                        imageFallbackSrc={order.asset_title_image?.url}
                        placeHolderImage={'/order-placeholder.svg'}
                    >
                        <AddressInfoCell address={order.address} customer_asset_reference={order.customer_asset_reference} />
                        <MandatorInfoCell order={order} customerUserList={customerUsers} />
                        <OrderInfoCell order={order} />
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="end"
                            gap={tokens.SizingSpacing200}
                            minWidth="310px"
                            flexGrow={1}
                        >
                            <Stack direction="column" gap={tokens.SizingSpacing100} alignItems="flex-end">
                                <OrderStatusLabel
                                    size="small"
                                    status={order.status}
                                    label={tTerminology(`orderStatuses.${order.status}.label`)}
                                />
                                {isOrderInPlausibility && (
                                    <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                        <Tooltip title={draftChipTooltipLabel} placement="top">
                                            <Box width="fit-content">
                                                <ContentChip size="small" highContrast color="orange" label={draftChipLabel} />
                                            </Box>
                                        </Tooltip>
                                        {showPlausibility && (
                                            <>
                                                <Tooltip title={annotationCountTooltipLabel} placement="top">
                                                    <Stack
                                                        direction="row"
                                                        gap={tokens.SizingSpacing075}
                                                        alignItems="center"
                                                        width="fit-content"
                                                    >
                                                        <ChatTextIcon
                                                            size={tokens.SizingIconS}
                                                            weight="bold"
                                                            color={tokens.ColorIconDefault}
                                                        />
                                                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                                                            {annotationCount}
                                                        </Typography>
                                                    </Stack>
                                                </Tooltip>
                                                <PlausibilityStatusChips
                                                    showInternalApproval={showInternalApproval}
                                                    isHandedOver={isHandedOver}
                                                    isInternallyApproved={isInternallyApproved}
                                                    isApproved={isApproved}
                                                />
                                            </>
                                        )}
                                    </Stack>
                                )}
                            </Stack>

                            {shouldShowPlausibilityActions && (
                                <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                    <IconButton
                                        size="small"
                                        variant="primary"
                                        icon={<HighlighterIcon />}
                                        onClick={(event) => {
                                            event.preventDefault()
                                            event.stopPropagation()

                                            if (plausibilityPath) {
                                                void navigate(plausibilityPath)
                                            }
                                        }}
                                        ariaLabel={t('plausibility.goToPlausibility')}
                                        tooltip={t('plausibility.goToPlausibility')}
                                    />
                                    <IconButtonWithOptions
                                        size="small"
                                        variant="secondary"
                                        icon={<CaretDownIcon />}
                                        ariaLabel={t('plausibility.actions')}
                                        tooltip={t('plausibility.actions')}
                                        optionTooltipPlacement="left"
                                        options={[
                                            {
                                                title: t('plausibility.goToPlausibility'),
                                                icon: <HighlighterIcon />,
                                                callback: () => {
                                                    if (plausibilityPath) {
                                                        void navigate(plausibilityPath)
                                                    }
                                                },
                                            },
                                            {
                                                title: t('plausibility.shareAnnotations'),
                                                icon: <PaperPlaneTiltIcon />,
                                                callback: () => onOpenHandoverModal(order),
                                                disabled: isHandoverDisabled,
                                                tooltip: isHandoverDisabled ? handoverTooltip : undefined,
                                            },
                                            ...(showInternalApproval
                                                ? [
                                                      {
                                                          title: t('plausibility.fundApproveDraft'),
                                                          icon: <SealCheckIcon />,
                                                          callback: () => onOpenInternalApprovalModal(order),
                                                          disabled: isInternallyApproved,
                                                          tooltip: isInternallyApproved ? t('plausibility.fundAlreadyApproved') : undefined,
                                                      },
                                                      {
                                                          title: t('plausibility.kvgApproveDraft'),
                                                          icon: <SealCheckIcon />,
                                                          callback: () => onOpenApprovalModal(order),
                                                          disabled: isApproved,
                                                          tooltip: isApproved ? t('plausibility.kvgAlreadyApproved') : undefined,
                                                      },
                                                  ]
                                                : [
                                                      {
                                                          title: t('plausibility.approveDraft'),
                                                          icon: <SealCheckIcon />,
                                                          callback: () => onOpenApprovalModal(order),
                                                          disabled: isApproved,
                                                          tooltip: isApproved ? t('plausibility.draftAlreadyApproved') : undefined,
                                                      },
                                                  ]),
                                        ]}
                                    />
                                </Stack>
                            )}
                        </Box>

                        <OrderObserverToggleButton order={order} onSuccess={onObserverSuccess} />
                    </ListCard>
                </Link>
            </ListItem>
        )
    }
)

function PlausibilityStatusChips({
    showInternalApproval,
    isHandedOver,
    isInternallyApproved,
    isApproved,
}: {
    showInternalApproval: boolean
    isHandedOver: boolean
    isInternallyApproved: boolean
    isApproved: boolean
}) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderListPage.plausibility' })
    const shouldShowHandoverChip = isHandedOver && !isApproved
    const shouldShowApprovalChips = showInternalApproval && (isInternallyApproved || isApproved)

    if (!showInternalApproval && isApproved) {
        return (
            <ContentChip
                size="small"
                color="green"
                label={t('approved')}
                icon={<SealCheckIcon size={tokens.SizingIconS} weight="fill" />}
            />
        )
    }

    if (!shouldShowHandoverChip && !shouldShowApprovalChips) {
        return null
    }

    return (
        <>
            {shouldShowHandoverChip && (
                <ContentChip
                    size="small"
                    color="blue"
                    label={t('shared')}
                    icon={<CheckCircleIcon size={tokens.SizingIconS} weight="fill" />}
                />
            )}
            {shouldShowApprovalChips && (
                <Tooltip
                    title={
                        <>
                            {isInternallyApproved ? t('fundApprovalGranted') : t('fundApprovalPending')}
                            <br />
                            {isApproved ? t('kvgApprovalGranted') : t('kvgApprovalPending')}
                        </>
                    }
                    placement="top"
                    disableInteractive
                >
                    <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                        <ApprovalStateChip label={t('fundLabel')} isApproved={isInternallyApproved} />
                        <ApprovalStateChip label={t('kvgLabel')} isApproved={isApproved} />
                    </Stack>
                </Tooltip>
            )}
        </>
    )
}

function ApprovalStateChip({ label, isApproved }: { label: string; isApproved: boolean }) {
    if (isApproved) {
        return <ContentChip size="small" color="green" label={label} icon={<SealCheckIcon size={tokens.SizingIconS} weight="fill" />} />
    }

    return (
        <Box
            sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                gap: tokens.SizingSpacing100,
                alignItems: 'center',
                width: 'fit-content',
                justifyContent: 'center',
                height: 'fit-content',
                borderRadius: tokens.SizingCornerS,
                padding: `${tokens.SizingSpacing025} ${tokens.SizingSpacing075}`,
                backgroundColor: tokens.ColorSurfaceDefault,
            }}
        >
            <SealCheckIcon size={tokens.SizingIconS} weight="fill" color={tokens.ColorIconDisabled} />
            <Typography variant="textS" color={tokens.ColorTextDisabled} noWrap>
                {label}
            </Typography>
        </Box>
    )
}

export default OrderListPageContent
